import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { mensagemErro, sessaoPerdida } from '@/pages/assistente-evento/utils';
import { MENSAGEM_ERRO_TECNICO } from '@/lib/mensagemAmigavel';
import type { EstadoSalvamento } from '@/pages/assistente-evento/types';

// ————————————————————————————————————————————————
// Erros: três tipos, três frases, zero jargão técnico.
// ————————————————————————————————————————————————

const FRASE_REDE =
  'Não consegui salvar agora. Seu conteúdo está guardado neste aparelho e vou tentar de novo.';
const FRASE_BANCO = 'Não consegui concluir esta alteração. Nada foi publicado.';

// Marcas técnicas que jamais aparecem para a pessoa (vão para o console).
const PADROES_TECNICOS =
  /(error\s*\d{4,5}|duplicate key|violates|constraint|does not exist|rpc|postgrest|unexpected|stack\s*trace|syntax error|relation|column|function|trigger|policy|fetch\s*failed|failed to fetch|networkerror)/i;

export type TipoErroAmigavel = 'rede' | 'banco';

export interface ErroAmigavel {
  tipo: TipoErroAmigavel;
  texto: string;
}

export function eErroDeRede(e: unknown): boolean {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
  const m = mensagemErro(e, '').toLowerCase();
  return /network|fetch|offline|timeout|abort|connection|sem conexão|internet/i.test(m);
}

function textoHumano(e: unknown): string | null {
  const m = mensagemErro(e, '');
  if (!m) return null;
  // Erro técnico já traduzido para a frase única: não mostra detalhe nenhum.
  if (m === MENSAGEM_ERRO_TECNICO) return null;
  if (PADROES_TECNICOS.test(m)) return null;
  return m;
}

export function erroAmigavel(e: unknown): ErroAmigavel {
  if (eErroDeRede(e)) return { tipo: 'rede', texto: FRASE_REDE };
  const humano = textoHumano(e);
  return { tipo: 'banco', texto: humano ? `${FRASE_BANCO} ${humano}` : FRASE_BANCO };
}

// ————————————————————————————————————————————————
// Motor de rascunho: criar uma vez, depois só atualizar.
// Proteção contra gravação fora de ordem via atualizado_em.
// ————————————————————————————————————————————————

type Estado = 'carregando' | 'erro' | 'ok';

export interface Retomada<T> {
  atualizado_em: string | null;
  draft: T;
}

export interface ConfigRascunho<T extends object> {
  /** Chave do localStorage que guarda o id do rascunho (ex.: rascunho:divulgar). */
  chaveLocal: string;
  /** Tabela onde o rascunho vive (precisa ter `id` e `atualizado_em`). */
  tabela: string;
  /** Rota para onde voltar depois de publicar / ao perder a sessão. */
  rotaVoltar: string;
  /** Draft vazio inicial. */
  draftInicial: T;
  /** Retoma um rascunho já criado nesta porta, ou null. */
  retomar: (id: number, userId: string) => Promise<Retomada<T> | null>;
  /** Monta as colunas prontas para gravar (pode gerar slug etc. — é assíncrono). */
  paraColunas: (draft: T, id: number | null) => Promise<Record<string, unknown>>;
  /** Cria o registro e devolve o id. Roda só uma vez. */
  criar: (colunas: Record<string, unknown>) => Promise<number>;
  /** Roda depois de criar/atualizar (ex.: subir a arte). */
  aposSalvar?: (id: number, draft: T) => Promise<void>;
  /** Conteúdo mínimo para considerar que vale salvar. */
  podeSalvar: (draft: T) => boolean;
  /** Só muda o estado para publicar. Deve ser idempotente. */
  publicarRegistro: (id: number) => Promise<void>;
  /** Rota pública para onde navegar depois de publicar. */
  destinoPublicado: () => string;
}

async function atualizarComVersao(
  tabela: string,
  id: number,
  colunas: Record<string, unknown>,
  versao: string | null,
): Promise<{ conflito: boolean; versao: string | null }> {
  const res = await supabase
    .from(tabela)
    .update(colunas)
    .eq('id', id)
    .eq('atualizado_em', versao)
    .select('atualizado_em');
  if (res.error) throw res.error;
  if (res.data && res.data.length > 0) {
    return { conflito: false, versao: (res.data[0] as { atualizado_em: string }).atualizado_em };
  }
  const { data: atual } = await supabase
    .from(tabela)
    .select('atualizado_em')
    .eq('id', id)
    .maybeSingle();
  return { conflito: true, versao: (atual?.atualizado_em as string) ?? null };
}

export function useRascunho<T extends object>(config: ConfigRascunho<T>) {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [registroId, setRegistroId] = useState<number | null>(null);
  const [draft, setDraft] = useState<T>(config.draftInicial);
  const [estadoSalvamento, setEstadoSalvamento] = useState<EstadoSalvamento>('ocioso');
  const [salvando, setSalvando] = useState(false);

  const configRef = useRef(config);
  configRef.current = config;

  const draftRef = useRef<T>(config.draftInicial);
  const registroIdRef = useRef<number | null>(null);
  const versaoRef = useRef<string | null>(null);
  const salvandoRef = useRef(false);
  const dirtyRef = useRef(false);
  const versaoDraftRef = useRef(0);
  const debounceRef = useRef<number | null>(null);
  const timerSalvoRef = useRef<number | null>(null);
  const salvarRef = useRef<() => Promise<boolean>>(async () => true);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  function mudarDraft(patch: Partial<T>) {
    versaoDraftRef.current += 1;
    setDraft((d) => ({ ...d, ...patch }) as T);
  }

  // Sem sessão de verdade (o contexto já sabe): manda para o login guardando o destino.
  function irParaLogin() {
    guardarDestino(config.rotaVoltar);
    navigate(`/entrar?voltar=${encodeURIComponent(config.rotaVoltar)}`, { replace: true });
  }

  async function garantirSessao(): Promise<boolean> {
    // Usa o estado do contexto (sem chamada de rede): não desloga por falha passageira.
    if (user) return true;
    irParaLogin();
    return false;
  }

  // Salva: cria uma vez, depois só atualiza pelo id, com trava de versão.
  async function salvar(): Promise<boolean> {
    if (salvandoRef.current) return false;
    const cfg = configRef.current;
    if (!cfg.podeSalvar(draftRef.current)) return true;

    salvandoRef.current = true;
    setEstadoSalvamento('salvando');
    const versaoInicio = versaoDraftRef.current;

    try {
      let colunas = await cfg.paraColunas(draftRef.current, registroIdRef.current);
      let id = registroIdRef.current;

      if (!id) {
        id = await cfg.criar(colunas);
        registroIdRef.current = id;
        setRegistroId(id);
        localStorage.setItem(cfg.chaveLocal, String(id));
        const { data: rec } = await supabase
          .from(cfg.tabela)
          .select('atualizado_em')
          .eq('id', id)
          .maybeSingle();
        versaoRef.current = (rec?.atualizado_em as string) ?? null;
      } else {
        let resultado = await atualizarComVersao(cfg.tabela, id, colunas, versaoRef.current);
        if (resultado.conflito) {
          // Outra gravação terminou antes: re-lê a versão e regrava com o
          // texto que a pessoa acabou de digitar (o texto dela vence).
          versaoRef.current = resultado.versao;
          colunas = await cfg.paraColunas(draftRef.current, id);
          resultado = await atualizarComVersao(cfg.tabela, id, colunas, versaoRef.current);
        }
        versaoRef.current = resultado.versao;
      }

      if (cfg.aposSalvar) await cfg.aposSalvar(id, draftRef.current);

      // Se digitou durante o save, agenda uma nova gravação.
      dirtyRef.current = versaoDraftRef.current !== versaoInicio;
      setEstadoSalvamento('salvo');
      if (timerSalvoRef.current) window.clearTimeout(timerSalvoRef.current);
      timerSalvoRef.current = window.setTimeout(() => setEstadoSalvamento('ocioso'), 2500);
      if (dirtyRef.current) {
        if (debounceRef.current) window.clearTimeout(debounceRef.current);
        debounceRef.current = window.setTimeout(() => {
          void salvarRef.current();
        }, 800);
      }
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      if (sessaoPerdida(e)) {
        // Erro de sessão NÃO é logout: renova e pede para tentar de novo.
        try {
          await supabase.auth.getSession();
        } catch {
          /* segue */
        }
        setErro('Não conseguimos confirmar seu acesso agora. Tente de novo.');
        setEstadoSalvamento('ocioso');
      } else {
        const amigavel = erroAmigavel(e);
        setErro(amigavel.texto);
        if (amigavel.tipo === 'rede') {
          setEstadoSalvamento('offline');
          dirtyRef.current = true;
        } else {
          setEstadoSalvamento('ocioso');
        }
      }
      return false;
    } finally {
      salvandoRef.current = false;
    }
  }

  salvarRef.current = salvar;

  // Carrega: sessão, rascunho retomável e estado inicial.
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino(configRef.current.rotaVoltar);
      navigate(`/entrar?voltar=${encodeURIComponent(configRef.current.rotaVoltar)}`, {
        replace: true,
      });
      return;
    }
    let ativo = true;
    const cfg = configRef.current;

    async function carregar() {
      setEstado('carregando');
      setErro(null);
      try {
        const draftId = localStorage.getItem(cfg.chaveLocal);
        if (draftId) {
          const idNum = Number(draftId);
          if (Number.isFinite(idNum) && idNum > 0) {
            const retomado = await cfg.retomar(idNum, user.id);
            if (retomado) {
              if (ativo) {
                registroIdRef.current = idNum;
                setRegistroId(idNum);
                versaoRef.current = retomado.atualizado_em;
                setDraft(retomado.draft);
                setEstado('ok');
              }
              return;
            }
            localStorage.removeItem(cfg.chaveLocal);
          }
        }

        if (ativo) {
          setDraft(cfg.draftInicial);
          setEstado('ok');
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
        if (ativo) {
          setErro(erroAmigavel(e).texto);
          setEstado('erro');
        }
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, pronto, user]);

  // Salvamento automático: 2s depois de parar de digitar.
  useEffect(() => {
    if (estado !== 'ok') return;
    if (!configRef.current.podeSalvar(draftRef.current)) return;

    dirtyRef.current = true;
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      void salvarRef.current();
    }, 2000);

    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [draft, estado]);

  // Quando a internet volta, tenta salvar o que ficou pendente.
  useEffect(() => {
    function aoOnline() {
      if (dirtyRef.current) void salvarRef.current();
    }
    window.addEventListener('online', aoOnline);
    return () => window.removeEventListener('online', aoOnline);
  }, []);

  // Publicar: só muda o estado do rascunho que já existe. Idempotente.
  async function publicar(): Promise<boolean> {
    setSalvando(true);
    try {
      if (!(await garantirSessao())) return false;

      // Garante que nada ficou pendente de salvar antes de publicar.
      const salvo = await salvarRef.current();
      if (!salvo) return false;

      const id = registroIdRef.current;
      if (!id) return false;

      await configRef.current.publicarRegistro(id);

      localStorage.removeItem(configRef.current.chaveLocal);
      navigate(configRef.current.destinoPublicado(), { state: { noAr: true } });
      return true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      if (sessaoPerdida(e)) {
        setErro('Não conseguimos confirmar seu acesso agora. Tente de novo.');
      } else {
        setErro(erroAmigavel(e).texto);
      }
      return false;
    } finally {
      setSalvando(false);
    }
  }

  return {
    estado,
    erro,
    setErro,
    registroId,
    draft,
    mudarDraft,
    setDraft,
    estadoSalvamento,
    salvando,
    salvar,
    publicar,
  };
}